import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useWeb3 } from '../context/Web3Context';
import { RankBadge } from '../components/RankBadge';
import { formatAddress } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
    Calendar,
    Trophy,
    Crown,
    Medal,
    Star,
    Users,
    Clock,
    Target,
    ChevronRight,
    Zap,
    Award,
    TrendingUp,
    Flame
} from 'lucide-react';
import { formatDistanceToNow, format, differenceInDays } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Season Status Badge
function SeasonStatusBadge({ status }) {
    const config = {
        active: { color: '#00FF66', label: 'Active', icon: Flame },
        upcoming: { color: '#FFD700', label: 'Upcoming', icon: Clock },
        completed: { color: '#9B6FE8', label: 'Completed', icon: Trophy },
        cancelled: { color: '#FF6B6B', label: 'Cancelled', icon: null }
    };
    
    const { color, label, icon: Icon } = config[status] || config.upcoming;
    
    return (
        <Badge style={{ backgroundColor: `${color}20`, color }}>
            {Icon && <Icon className="w-3 h-3 mr-1" />}
            {label}
        </Badge>
    );
}

// Season Card
function SeasonCard({ season, currentAddress }) {
    const startDate = new Date(season.start_date);
    const endDate = new Date(season.end_date);
    const now = new Date();
    
    const totalDays = differenceInDays(endDate, startDate);
    const daysElapsed = differenceInDays(now, startDate);
    const progress = season.status === 'completed' ? 100 : Math.max(0, Math.min(100, (daysElapsed / totalDays) * 100));
    
    // Find user's rank in leaderboard
    const userEntry = season.leaderboard?.find(e => e.address === currentAddress?.toLowerCase());
    
    return (
        <Card className="bg-white/[0.02] border-white/5 hover:border-[#FFD700]/30 transition-all">
            <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <SeasonStatusBadge status={season.status} />
                        <h3 className="text-xl font-bold text-white mt-2">{season.name}</h3>
                        <p className="text-white/50 text-sm mt-1">{season.description}</p>
                    </div>
                    {season.on_chain_id && (
                        <Badge className="bg-[#FFD700]/20 text-[#FFD700]">
                            <Zap className="w-3 h-3 mr-1" />
                            On-chain
                        </Badge>
                    )}
                </div>
                
                {/* Progress */}
                {season.status !== 'upcoming' && (
                    <div className="mb-4">
                        <div className="flex justify-between text-xs text-white/40 mb-1">
                            <span>{format(startDate, 'MMM d')}</span>
                            <span>{Math.round(progress)}% complete</span>
                            <span>{format(endDate, 'MMM d')}</span>
                        </div>
                        <Progress value={progress} className="h-1.5 bg-white/10" />
                    </div>
                )}
                
                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="text-center p-3 rounded-lg bg-white/[0.03]">
                        <Trophy className="w-5 h-5 text-[#FFD700] mx-auto mb-1" />
                        <div className="text-white font-bold">{season.tournament_count || season.tournaments?.length || 0}</div>
                        <div className="text-xs text-white/40">Tournaments</div>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-white/[0.03]">
                        <Users className="w-5 h-5 text-[#00FF66] mx-auto mb-1" />
                        <div className="text-white font-bold">{season.leaderboard?.length || 0}</div>
                        <div className="text-xs text-white/40">Competitors</div>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-white/[0.03]">
                        <Star className="w-5 h-5 text-[#9B6FE8] mx-auto mb-1" />
                        <div className="text-white font-bold">{season.prize_pool || 0}</div>
                        <div className="text-xs text-white/40">ETH Prizes</div>
                    </div>
                </div>
                
                {/* User's Position */}
                {userEntry && (
                    <div className="p-3 rounded-lg bg-[#00FF66]/10 border border-[#00FF66]/30 mb-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Medal className="w-4 h-4 text-[#00FF66]" />
                                <span className="text-white text-sm">Your Rank</span>
                            </div>
                            <div className="text-right">
                                <span className="text-[#00FF66] font-bold">#{userEntry.rank}</span>
                                <span className="text-white/40 text-sm ml-2">({userEntry.points} pts)</span>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Top 3 Preview */}
                {season.leaderboard && season.leaderboard.length > 0 && (
                    <div className="space-y-2">
                        <div className="text-xs text-white/40 mb-2">Season Leaders</div>
                        {season.leaderboard.slice(0, 3).map((entry, idx) => {
                            return (
                                <div 
                                    key={entry.address}
                                    className="flex items-center justify-between p-2 rounded-lg bg-white/[0.02]"
                                >
                                    <div className="flex items-center gap-2">
                                        <RankBadge rank={idx + 1} size="sm" />
                                        <span className="text-white text-sm font-mono">
                                            {formatAddress(entry.address)}
                                        </span>
                                    </div>
                                    <span className="text-[#FFD700] font-bold">{entry.points} pts</span>
                                </div>
                            );
                        })}
                    </div>
                )}
                
                {/* View Details */}
                <Link to={`/seasons/${season.id}`}>
                    <Button className="w-full mt-4 bg-white/5 text-white hover:bg-white/10">
                        View Season Details
                        <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                </Link>
            </CardContent>
        </Card>
    );
}

// Point System Info
function PointSystemCard() {
    const points = [
        { place: '1st Place', pts: 100, color: '#FFD700' },
        { place: '2nd Place', pts: 70, color: '#C0C0C0' },
        { place: '3rd Place', pts: 50, color: '#CD7F32' },
        { place: 'Top 10', pts: 25, color: '#9B6FE8' },
        { place: 'Participant', pts: 10, color: '#00FF66' }
    ];
    
    return (
        <Card className="bg-white/[0.02] border-white/5">
            <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Target className="w-5 h-5 text-[#FFD700]" />
                    Point System
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-3">
                    {points.map(({ place, pts, color }) => (
                        <div key={place} className="flex items-center justify-between">
                            <span className="text-white/60 text-sm">{place}</span>
                            <Badge style={{ backgroundColor: `${color}20`, color }}>
                                +{pts} pts
                            </Badge>
                        </div>
                    ))}
                </div>
                <p className="text-white/40 text-xs mt-4">
                    Points accumulate across all tournaments in a season
                </p>
            </CardContent>
        </Card>
    );
}

export function SeasonsPage() {
    const { address, isConnected } = useWeb3();
    const [seasons, setSeasons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [userStats, setUserStats] = useState([]);
    
    useEffect(() => {
        fetchSeasons();
    }, []);
    
    useEffect(() => {
        if (address) {
            fetchUserStats();
        }
    }, [address]);
    
    const fetchSeasons = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/seasons?limit=20`);
            if (response.ok) {
                const data = await response.json();
                setSeasons(data);
            }
        } catch (error) {
            console.error('Failed to fetch seasons:', error);
        } finally {
            setLoading(false);
        }
    };
    
    const fetchUserStats = async () => {
        if (!address) return;
        try {
            const response = await fetch(`${API_URL}/api/seasons/user/${address}`);
            if (response.ok) {
                const data = await response.json();
                setUserStats(data);
            }
        } catch (error) {
            console.error('Failed to fetch user stats:', error);
        }
    };
    
    const filteredSeasons = seasons.filter(s => 
        filter === 'all' || s.status === filter
    );
    
    // Calculate total user points across all seasons
    const totalPoints = userStats.reduce((sum, s) => sum + (s.points || 0), 0);
    const bestRank = userStats.filter(s => s.rank).reduce((best, s) => Math.min(best, s.rank), Infinity);
    
    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 page-enter" data-testid="seasons-page">
            {/* Header */}
            <div className="text-center mb-8 animate-fade-in-down">
                <div className="flex items-center justify-center gap-3 mb-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FFD700]/20 to-[#FF6B6B]/20 flex items-center justify-center">
                        <Calendar className="w-7 h-7 text-[#FFD700]" />
                    </div>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                    Seasonal <span className="text-[#FFD700]">Championships</span>
                </h1>
                <p className="text-white/50 max-w-md mx-auto">
                    Compete across multiple tournaments to earn season points and climb the rankings
                </p>
            </div>
            
            {/* User Stats (if connected) */}
            {isConnected && userStats.length > 0 && (
                <Card className="bg-gradient-to-r from-[#FFD700]/10 to-[#9B6FE8]/10 border-[#FFD700]/30 mb-8 animate-fade-in-up delay-1">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-white/50 text-sm mb-1">Your Season Performance</div>
                                <div className="flex items-center gap-6">
                                    <div>
                                        <div className="text-3xl font-bold text-[#FFD700]">{totalPoints}</div>
                                        <div className="text-white/40 text-sm">Total Points</div>
                                    </div>
                                    {bestRank !== Infinity && (
                                        <div>
                                            <div className="text-3xl font-bold text-[#00FF66]">#{bestRank}</div>
                                            <div className="text-white/40 text-sm">Best Rank</div>
                                        </div>
                                    )}
                                    <div>
                                        <div className="text-3xl font-bold text-white">{userStats.length}</div>
                                        <div className="text-white/40 text-sm">Seasons</div>
                                    </div>
                                </div>
                            </div>
                            <TrendingUp className="w-12 h-12 text-[#FFD700]/30" />
                        </div>
                    </CardContent>
                </Card>
            )}
            
            {/* Filter Tabs */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                {['all', 'active', 'upcoming', 'completed'].map(status => (
                    <Button
                        key={status}
                        variant={filter === status ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setFilter(status)}
                        className={filter === status 
                            ? 'bg-[#FFD700] text-[#0A0A0C]' 
                            : 'border-white/10 text-white/60'
                        }
                    >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Button>
                ))}
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Seasons List */}
                <div className="lg:col-span-2 space-y-6">
                    {loading ? (
                        <div className="space-y-4">
                            {[1, 2].map(i => (
                                <Skeleton key={i} className="h-80 bg-white/5" />
                            ))}
                        </div>
                    ) : filteredSeasons.length === 0 ? (
                        <Card className="bg-white/[0.02] border-white/5">
                            <CardContent className="p-12 text-center">
                                <Calendar className="w-12 h-12 text-white/20 mx-auto mb-4" />
                                <h3 className="text-xl font-semibold text-white mb-2">No Seasons Found</h3>
                                <p className="text-white/50">
                                    {filter !== 'all' 
                                        ? `No ${filter} seasons at the moment.`
                                        : 'Check back soon for seasonal championships!'
                                    }
                                </p>
                            </CardContent>
                        </Card>
                    ) : (
                        filteredSeasons.map(season => (
                            <SeasonCard 
                                key={season.id} 
                                season={season}
                                currentAddress={address}
                            />
                        ))
                    )}
                </div>
                
                {/* Sidebar */}
                <div className="space-y-6">
                    <PointSystemCard />
                    
                    {/* How It Works */}
                    <Card className="bg-white/[0.02] border-white/5">
                        <CardHeader>
                            <CardTitle className="text-lg text-white flex items-center gap-2">
                                <Award className="w-5 h-5 text-[#00FF66]" />
                                How Seasons Work
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="text-white/50 text-sm space-y-3">
                            <p>
                                <span className="text-white font-semibold">1.</span> Compete in tournaments during the season
                            </p>
                            <p>
                                <span className="text-white font-semibold">2.</span> Earn points based on your tournament finishes
                            </p>
                            <p>
                                <span className="text-white font-semibold">3.</span> Climb the seasonal leaderboard
                            </p>
                            <p>
                                <span className="text-white font-semibold">4.</span> Top performers earn exclusive rewards
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
